import React, { useCallback } from 'react'
import {
  faBell,
  faBullseye,
  faChartLine,
  faCircleInfo,
  faFileLines,
  faGear,
  faRightFromBracket,
  faRotate,
  faTable,
  faUser,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import styled from 'styled-components'
import { colors } from '~/design'
import Link from '~/prix/react/components/link'
import UserIcon from '~/components/icons/users/48px_circle-08.svg'
import { version } from '~/utils/client/publicConfig'
import keycloakConfig from '~/utils/shared/keycloakConfig'
import { useKeycloak } from '~/components/keycloak'
import useAPI from '~/prix/react/hooks/api'
import Menu from '~/prix/react/components/menu/menu'
import { useNavigate } from 'react-router-dom'
import Card from '~/prix/react/components/card'

interface IOptionsMenuProps {
  menuActive?: string
}

export const Version = styled.div`
  padding-top: 25px;
  text-align: center;
  justify-self: flex-end;
`

export const Aside = styled(Card)`
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  position: relative;
  margin-top: 32px;

  .header-card {
    font-size: 16px;
    background: ${({ theme }) => theme.colors.primary};
    display: flex;
    flex-direction: column;
    align-items: center;
    border-radius: 8px 8px 0 0;
    margin: -15px -15px 0 -15px;
    padding: 32px 10px 15px;

    .user-photo-container {
      border-radius: 50%;
      object-fit: cover;
      height: 64px;
      width: 64px;
      position: absolute;
      top: 0;
      left: 50%;
      transform: translate(-50%, -50%);
      background-color: ${({ theme }) => theme.colors.primary};
      box-shadow: ${({ theme }) => theme.shadow(1)};
      display: flex;
      align-items: center;
      justify-content: center;

      svg {
        height: 64px;
        width: 64px;
      }
    }

    .header-user-info {
      display: flex;
      flex-direction: column;
      line-height: 22px;
      color: #fff;
      font-size: 19px;
      padding-top: 3px;
      text-align: center;
      overflow: hidden;
      max-width: 100%;

      strong,
      small {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      small {
        font-size: 14px;
      }
    }
  }

  ul {
    margin: 15px 0 0;
    padding: 0;
    flex-direction: column;
    width: 100%;
    list-style-type: none;
    display: flex;
    flex-direction: column;
    gap: 4px;

    li {
      text-decoration: none;
      line-height: 1.25;

      a {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        border-radius: 2px;
        padding: 0.5rem 0.75rem;
        color: #394d60;
        font-size: 16px;
        border-radius: ${({ theme }) => theme.borderRadius};

        &:hover {
          background-color: rgb(245, 245, 245);
        }

        &.is-active {
          display: flex;
          align-self: center;
          color: #fff;
          background-color: ${colors.sebraeBlue};
        }
      }
    }

    hr {
      border: none;
      border-top: 1px solid ${({ theme }) => theme.colors.lightGrey};
      margin: 8px 0;
    }
  }
`

const UserPhoto = styled.img`
  display: block;
  height: 62px;
  width: 62px;
  border-radius: 50%;
  object-fit: cover;
`

const MONITOR_USAGE_ANALYTICS_SUMMARY_ROLES = new Set<string>([
  'administrator',
  'systemAdministrator',
])

export default function OptionsMenu({ menuActive }: IOptionsMenuProps) {
  const { context } = useAPI()
  const { logout } = useKeycloak()
  const navigate = useNavigate()
  const userPhotoUrl = context.user?.photoUrl ?? null

  const canShowMonitorUsageAnalyticsSummary = Boolean(
    context.user?.roles?.some(role => MONITOR_USAGE_ANALYTICS_SUMMARY_ROLES.has(role)),
  )

  const handleReload = useCallback(async (event: React.MouseEvent) => {
    event.preventDefault()
    if (window.caches) {
      try {
        const keyList = await window.caches.keys()
        await Promise.all(keyList.map(key => window.caches.delete(key)))
      } catch (error) {
        console.error(error)
      }
    }

    try {
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations()
        for (const registration of registrations) {
          await registration.unregister()
        }
      }
    } catch (error) {
      console.error(error)
    }
    window.document.location.href = `/index.html?refresh=${Math.random()}`
  }, [])

  const handleLogout = useCallback(
    async (event: React.MouseEvent) => {
      event.preventDefault()
      navigate('/')
      await logout().catch((error: Error) => console.error(error))
    },
    [logout, navigate],
  )

  return (
    <Menu style={{ justifyContent: 'space-between', padding: '24px' }}>
      <Aside>
        <div className='header-card'>
          <div className='user-photo-container'>
            {userPhotoUrl ? <UserPhoto src={userPhotoUrl} /> : <UserIcon fill='#fff' />}
          </div>
          <div className='header-user-info'>
            <strong>{context.user?.name}</strong>
            <small>{context.user?.email}</small>
          </div>
        </div>
        <ul>
          <li>
            <Link
              href='/app/options/monitor-permissions'
              className={menuActive === 'monitor-permissions' ? 'is-active' : undefined}
            >
              <FontAwesomeIcon icon={faTable} fixedWidth />
              Tópicos de interesse
            </Link>
          </li>
          {canShowMonitorUsageAnalyticsSummary && (
            <li>
              <Link
                href='/app/options/monitor-usage-analytics-summary'
                className={
                  menuActive === 'monitor-usage-analytics-summary' ? 'is-active' : undefined
                }
              >
                <FontAwesomeIcon icon={faChartLine} fixedWidth />
                Uso da plataforma
              </Link>
            </li>
          )}
          <li>
            <Link
              href='/app/options/notifications'
              className={menuActive === 'notifications' ? 'is-active' : undefined}
            >
              <FontAwesomeIcon icon={faBell} fixedWidth />
              Notificações
            </Link>
          </li>
          <li>
            <Link
              href='/app/options/system-and-information'
              className={menuActive === 'system-and-information' ? 'is-active' : undefined}
            >
              <FontAwesomeIcon icon={faCircleInfo} fixedWidth />
              Sistema e informações
            </Link>
          </li>
          <li>
            <a href={`/index.html?refresh=${Math.random()}`} onClick={handleReload}>
              <FontAwesomeIcon icon={faRotate} fixedWidth />
              Atualizar
            </a>
          </li>
          <hr />
          <li>
            <Link
              href={`${keycloakConfig.url}/realms/${keycloakConfig.realm}/account`}
              remote
              newTab
            >
              <FontAwesomeIcon icon={faUser} fixedWidth />
              Conta de usuário
            </Link>
          </li>
          <li>
            <Link href='/terms' remote newTab>
              <FontAwesomeIcon icon={faFileLines} fixedWidth />
              Termos de uso
            </Link>
          </li>
          <li>
            <Link onClick={handleLogout} style={{ color: '#e74c3c' }}>
              <FontAwesomeIcon icon={faRightFromBracket} fixedWidth />
              Sair
            </Link>
          </li>
        </ul>
      </Aside>
      <Version>
        <span>Versão {version}</span>
      </Version>
    </Menu>
  )
}
